import {useQueryClient} from '@tanstack/react-query';
import {CreatorSafetyReport} from '@/types/creatorSafetyReport';
import QueryContext from '@/api/QueryContext';
import vettingFetchers from '@/api/fetchers/vetting';
import useDataQuery from '@/hooks/use-data-query';

export const queryKey = 'vettingReport';
const summaryQueryKey = 'vettingReportSummary';
const SUMMARY_LIMIT_PER_FLAG = 5;

function useVettingReportQuery({
  brandId,
  creatorId,
  reportId,
  scope,
}: {
  brandId: string;
  creatorId: string;
  reportId: string;
  scope?: 'brand' | 'agency';
}) {
  const enabled = !!brandId && !!creatorId && !!reportId;
  const queryClient = useQueryClient();

  // Phase 1: Summary query — returns fast with up to 5 posts per flag
  const summaryResponse = useDataQuery<CreatorSafetyReport>({
    queryKey: [summaryQueryKey, creatorId, reportId, scope],
    queryFn: (context: QueryContext) =>
      vettingFetchers.report(context, {limitPerFlag: SUMMARY_LIMIT_PER_FLAG}),
    enabled,
    cacheTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
    meta: {
      brandId,
      creatorId,
      reportId,
      scope,
    },
  });

  // Phase 2: Full data query — fires in parallel to summary query and removes summary query when full data is ready
  const fullResponse = useDataQuery<CreatorSafetyReport>({
    queryKey: [queryKey, creatorId, reportId, scope],
    queryFn: vettingFetchers.report,
    enabled,
    cacheTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
    meta: {
      brandId,
      creatorId,
      reportId,
      scope,
    },
    onSuccess: () => {
      queryClient.removeQueries([summaryQueryKey, creatorId, reportId]);
    },
  });

  // Use full data when available, otherwise summary
  const activeResponse = fullResponse.isSuccess
    ? fullResponse
    : summaryResponse;
  const isLoadingFullData = !fullResponse.isSuccess;

  return {
    ...activeResponse,
    isLoadingFullData,
  };
}

export default useVettingReportQuery;
